import React, { useState, useEffect, useRef } from "react";
import { Skeleton } from "@mui/material";
import ReactPaginate from "react-paginate";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";
import { useNavigate, useLocation } from "react-router-dom";

import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import Messages from "../../../constants/Messages";
import handleError from "../../../constants/handleError";

import DeleteModal from "../../../constants/DeleteModal";
import InvoiceTemp from "./InvoiceTemp";
import { useAuth } from "../../../context/AuthProvider";
import { usePermission } from "../../../context/PermissionContext";
import useKeyboardTableNavigation from "../../../constants/useKeyboardTableNavigation";
import Table from "../../../constants/Table";
import { AddIcon, DeleteIcon, EditIcon, RefreshIcon } from "../../../constants/Icons";

const MyList = ({ isActive }) => {
  const [isBtnLoading, setBtnLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEstimateData, setEstimateData] = useState([]);
  const containerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const perPage = 10;

  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [isViewData, setViewData] = useState(null);
  const [isDeleteData, setDeleteData] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  // const [estimate, setEstimation] = useState([]);

  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const { isUser } = useAuth();
  const { permission, hasPermission, globelPopupOpen, storeSettings } =
    usePermission();
  const navigate = useNavigate();
  const location = useLocation(); // Access location to get state

  const handleListEstimate = async (page = 0) => {
    try {
      if (!checkNetwork()) return;
      setIsLoading(true);
      const data = { pageno: page + 1, limit: perPage };
      const response = await AxiosInstance.get(
        `module/estimate/user/list?pageno=${page + 1}&limit=${perPage}`,
        // data
      );
      const results = response.data.results || [];
      setEstimateData(results);
      if (results.length === 0) {
        setTotalPages(0);
        setTotalItems(0);
        setCurrentPage(0);
      } else {
        setTotalPages(response.data.totalpages || 0);
        setTotalItems(response.data.totalitems || 0);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = async (item) => {
    if (
      hasPermission("ESTIMATE", "ESTIMATE:view") ||
      permission === undefined
    ) {
      try {
        const response = await AxiosInstance.get(`module/estimate/${item._id}`);
        const fullData = {
          ...response?.data?.results,
          settings: storeSettings.paymentSettings,
        };
        setViewData(fullData);
        setViewModalOpen(true);
      } catch (error) {
        console.error("Error fetching setting data:", error);
        showAlert("error", "Failed to fetch setting data");
      }
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleClose = () => {
    setViewModalOpen(false);
    setViewData(null);
    // Clear the location state to prevent re-opening the modal
    navigate(location.pathname, { replace: true, state: {} });
  };

  const handleEdit = (item) => {
    if (
      hasPermission("ESTIMATE", "ESTIMATE:updateItem") ||
      permission === undefined
    ) {
      const navigatelist = "/mylist";
      navigate(`/editestimate`, {
        state: { item, navigatelist },
        replace: false,
        key: item._id,
      });
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const openDeleteModal = (cItem) => {
    if (
      hasPermission("ESTIMATE", "ESTIMATE:delete") ||
      permission === undefined
    ) {
      setDeleteData(cItem);
      setDeleteModalOpen(true);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleDelete = async () => {
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.delete(
        `module/estimate/${isDeleteData?._id}`,
        // {
        //   data: {
        //     estimateid: isDeleteData?._id,
        //   },
        // }
      );
      showAlert("success", Messages.SUCCESS.ESTIMATE_DELETED);

      const newTotal = totalItems - 1;
      const newTotalPages = Math.ceil(newTotal / perPage);

      if (isEstimateData.length === 1 && currentPage > 0) {
        setCurrentPage((prev) => prev - 1);
        await handleListEstimate(currentPage - 1);
      } else {
        await handleListEstimate(currentPage);
      }

      setTotalItems(newTotal);
      setTotalPages(newTotalPages);
      setDeleteModalOpen(false);

      closeDeleteModal();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const closeDeleteModal = () => {
    setDeleteData(null);
    setDeleteModalOpen(false);
  };

  // const getColor = (status) => {
  //   switch (status?.toLowerCase()) {
  //     case "active":
  //       return "bg-label-success";
  //     case "cancelled":
  //       return "bg-label-danger";
  //     default:
  //       return "bg-label-secondary";
  //   }
  // };

  // const handleLink = async () => {
  //   if (
  //     hasPermission("ESTIMATE", "ESTIMATE:createNumber") ||
  //     permission === undefined
  //   ) {
  //     try {
  //       const estimate = await handleGetEstimation(); // wait for the data
  //       const navigatelist="/mylist";
  //       if (estimate) {
  //         navigate("/estimate", { state: { estimate,navigatelist } }); // pass in `state`
  //       }
  //     } catch (err) {
  //       console.error("Navigation failed:", err);
  //     }
  //   } else {
  //     showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
  //   }
  // };

  const handleRefresh = async () => {
    await handleListEstimate(currentPage);
  };

  const handleLink = () => {
    if (
      hasPermission("ESTIMATE", "ESTIMATE:createNumber") ||
      permission === undefined
    ) {
      const navigatelist = "/listestimation";
      navigate("/estimate", { state: { navigatelist } });
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleDone = () => {
    setViewModalOpen(false);
    navigate("/mylist");
  };

  useKeyboardTableNavigation({
    isActive,
    containerRef,
    modals: {
      isDeleteModalOpen,
      isViewModalOpen,
      globelPopupOpen,
    },
    onEnter: handleView,
  });

  useEffect(() => {
    handleListEstimate(currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (location.state?.modalData) {
      setViewData(location.state.modalData);
      setViewModalOpen(true);
    }
  }, [location.state]);

  return (
    <div className="page-wrapper">
      <div className="page-content">
        <div className="page-breadcrumb d-flex align-items-center mb-3 gap-2">
          <button
            type="button"
            className="btn btn-sm btn-light p-1 shadow-none"
            onClick={handleRefresh}
            disabled={isLoading}
            title="Refresh list"
          >
            <RefreshIcon iClass={`${isLoading ? "bx-spin" : ""}`}/>
          </button>

          <div className="breadcrumb-title pe-3">List Estimate</div>
          {/* <div className="ms-auto">
            <button
              type="button"
              className="btn btn-primary mt-2 mt-lg-0"
              onClick={handleLink}
            >
              <i className="ng ng-ng-add-icon"></i> Add Estimate
            </button>
          </div> */}
        </div>

        <div className="row">
          <div className="col">
            <div className="card radius-10 mb-0">
              <div className="card-body px-0">
                <div className="row px-3">
                  <div className="col-lg-6  col-6 d-flex align-items-center">
                    <div>
                      <strong>Total Estimate:</strong>{" "}
                      <span className="badge btn-label-primary fs-15 ">
                        {totalItems || 0}
                      </span>
                    </div>
                  </div>
                  <div className="col-lg-6 col-6 d-flex justify-content-end ">
                    <button
                      type="button"
                      className="btn btn-primary mt-2 mt-lg-0"
                      onClick={handleLink}
                    >
                      <AddIcon/> Add Estimate
                    </button>
                  </div>
                </div>

                <div ref={containerRef}>
                  <Table
                    loading={isLoading}
                    data={isEstimateData}
                    onRowClick={(item) => handleView(item)}
                    columns={[
                      {
                        label: "S.No",
                        render: (_, index) => (
                          <>{currentPage * perPage + index + 1}</>
                        ),
                      },
                      {
                        label: "Estimate No",
                        render: (item) => (
                          <span className="badge rounded-pill bg-label-deep-purple">
                            {item?.estimateNo}
                          </span>
                        ),
                      },
                      {
                        label: "Estimate Date",
                        render: (item) => (
                          <span className="text-heading fw-medium text-truncate">
                            {
                              DateConverter.ISOToDisplayFormat(
                                DateConverter.convertToTimeZone(
                                  item?.estimateDate,
                                  isUser?.timezone,
                                ),
                              ).date
                            }
                          </span>
                        ),
                      },
                      {
                        label: "Estimate To",
                        render: (item) => (
                          <div className="d-flex flex-column">
                            <span className="text-nowrap text-heading fw-medium">
                              {item.estimateTo ? `${item.estimateTo.name}` : ""}
                            </span>
                            <span className="text-nowrap">
                              <small>
                                {item.estimateTo
                                  ? `+91 ${item.estimateTo.phone}`
                                  : " - "}
                              </small>
                            </span>
                          </div>
                        ),
                      },
                      {
                        label: "Estimate Items",
                        render: (item) => (
                          <>{item?.estimateItemQuantity || 0} Item(s)</>
                        ),
                      },
                      {
                        label: "Grand Total",
                        render: (item) => (
                          <span className="text-heading fw-medium text-truncate">
                            ₹
                            {CurrencyConverter.toCurrency(
                              Number(item.grandTotal) || 0,
                              "INR",
                            )}
                          </span>
                        ),
                      },
                      {
                        label: "Actions",
                        render: (item) => (
                          <div className="order-actions opacity-0 opacity-md-100">
                            {/* <button
                                  type="button"
                                  className="btn-label-info shadow-none me-2"
                                  onClick={(e) => {
                                          e.stopPropagation();
                                           handleView(item)}}
                                >
                                  <i className="bx bx-show"></i>
                                </button> */}
                            {item?.status !== "ACTIVE" && (
                              <>
                                <button
                                  type="button"
                                  className="btn-label-primary shadow-none me-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(item);
                                  }}
                                >
                                  <EditIcon/>
                                </button>
                                <button
                                  type="button"
                                  className="btn-label-danger shadow-none me-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteModal(item);
                                  }}
                                >
                                  <DeleteIcon/>
                                </button>
                              </>
                            )}
                          </div>
                        ),
                      },
                    ]}
                  />
                </div>
                {totalPages > 0 && (
                  <div className="d-flex justify-content-center mt-3">
                    <ReactPaginate
                      previousLabel={"← Previous"}
                      nextLabel={"Next →"}
                      breakLabel={"..."}
                      onPageChange={({ selected }) => {
                        setIsLoading(true);
                        setCurrentPage(selected);
                      }}
                      pageRangeDisplayed={5}
                      pageCount={totalPages}
                      containerClassName="pagination"
                      pageClassName="page-item"
                      pageLinkClassName="page-link"
                      previousClassName="page-item"
                      previousLinkClassName="page-link"
                      nextClassName="page-item"
                      nextLinkClassName="page-link"
                      breakClassName="page-item"
                      breakLinkClassName="page-link"
                      activeClassName="active"
                      forcePage={currentPage}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {isViewModalOpen && (
          <InvoiceTemp
            data={isViewData}
            onClose={handleClose}
            onEdit={handleEdit}
            onDone={handleDone}
          />
        )}

        {isDeleteModalOpen && (
          <DeleteModal
            onClose={() => setDeleteModalOpen(false)}
            onConfirm={handleDelete}
            isLable={" Estimation " + isDeleteData?.estimateno}
            isBtnLoading={isBtnLoading}
          />
        )}
      </div>
    </div>
  );
};

export default MyList;
